import type { HealthEventOut } from "../types";

/**
 * Which recent health events deserve the clay "Health note" badge.
 *
 * `health_events` has no severity or follow-up column (`app/models/
 * animals.py::HealthEvent`: `event_type`, `event_date`, `note`, attachment,
 * archive only), and this change adds no migration, so attention is
 * derived from what IS recorded:
 *
 * 1. The event type. `dewormer`, `vaccine`, `farrier` and `dental` are
 *    scheduled husbandry: routine. `vet` and `other` are where an injury,
 *    illness or treatment gets logged (there is no dedicated injury/
 *    illness/treatment type), so they need attention by default. An
 *    unrecognized type is treated as needing attention.
 * 2. The note can only UPGRADE a routine event: a farrier visit that notes
 *    "limping, left hind" still needs eyes on the animal. Erring toward
 *    the badge is deliberate; a missed lame horse costs far more than an
 *    extra chip.
 * 3. The note can DOWNGRADE a vet/other event only on an explicit
 *    all-clear ("routine check, no concerns") with no concern word and no
 *    "but"/"however" anywhere in it.
 *
 * Routine events still show, as a neutral "Routine care" chip, so nothing
 * recent disappears from the card; it just stops reading as an alarm.
 */

export type HealthNoteLevel = "attention" | "routine";

const ROUTINE_TYPES = new Set(["dewormer", "vaccine", "farrier", "dental"]);

const CONCERN_PATTERN =
  /\b(limp\w*|lame\w*|injur\w*|wound\w*|laceration\w*|bleed\w*|swell\w*|swollen|sick\w*|ill|illness|fever\w*|cough\w*|diarrh\w*|scour\w*|abscess\w*|colic\w*|bloat\w*|off feed|not eating|lethargic|treated|treating|treatment|antibiotic\w*|infect\w*|mastitis|prolapse\w*|follow[- ]?up|recheck|monitor\w*|watch)\b/i;

const ALL_CLEAR_PATTERN =
  /\b(no (concerns?|issues?|problems?|findings)|all clear|all good|nothing (found|wrong|of note)|clean bill)\b/i;

const CAVEAT_PATTERN = /\b(but|however|except|although)\b/i;

export function healthEventLevel(
  event: Pick<HealthEventOut, "event_type" | "note">,
): HealthNoteLevel {
  const note = event.note ?? "";
  const hasConcern = CONCERN_PATTERN.test(note);
  if (ROUTINE_TYPES.has(event.event_type)) return hasConcern ? "attention" : "routine";
  if (!hasConcern && ALL_CLEAR_PATTERN.test(note) && !CAVEAT_PATTERN.test(note)) return "routine";
  return "attention";
}

/** The strongest level among `events`, or `null` when there are none. */
export function strongestLevel(
  events: Array<Pick<HealthEventOut, "event_type" | "note">>,
): HealthNoteLevel | null {
  let level: HealthNoteLevel | null = null;
  for (const event of events) {
    const next = healthEventLevel(event);
    if (next === "attention") return "attention";
    level = next;
  }
  return level;
}
